import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Lock, ShieldCheck, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/DesignSystem';
import { loadStripe, STRIPE_APPEARANCE, stripeReturnUrl, stripeTestMode, StripeElements, StripeInstance } from '../../services/stripeClient';
import { formatKg } from '../../services/shipping';
import type { VatLine } from '../../services/vat';
import { VatLines } from './VatLines';

interface StripePaymentProps {
    clientSecret: string;
    goods: number;
    shipping: number;
    /** Frakten visas bara vid hemleverans. */
    isHome: boolean;
    weightGrams: number | null;
    /** Moms per sats från databasen. */
    vatBreakdown?: VatLine[];
    itemCount: number;
    email: string;
    /** Betalningen gick igenom (eller behandlas, t.ex. Klarna). */
    onPaid: (status: 'succeeded' | 'processing') => void;
    /** Strax innan Stripe kan lämna sidan (Swish-appen, Klarna, 3D Secure). */
    onBeforeRedirect: () => void;
    onBack: () => void;
}

const kr = (n: number) => `${n.toLocaleString('sv-SE')} kr`;

/**
 * Steg 3a i kassan: Stripes betalfält för varor och frakt. Fältet är Stripes
 * eget (iframe från js.stripe.com) och visar de betalsätt som är påslagna i
 * Stripe: kort, Apple Pay, Google Pay och Swish (Klarna är avstängt, se docs/frakt/).
 */
export const StripePayment: React.FC<StripePaymentProps> = ({ clientSecret, goods, shipping, isHome, weightGrams, vatBreakdown, itemCount, email, onPaid, onBeforeRedirect, onBack }) => {
    const mountRef = useRef<HTMLDivElement>(null);
    const stripeRef = useRef<StripeInstance | null>(null);
    const elementsRef = useRef<StripeElements | null>(null);
    const [ready, setReady] = useState(false);
    const [complete, setComplete] = useState(false);
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const total = goods + shipping;

    useEffect(() => {
        let cancelled = false;
        let element: { unmount: () => void } | null = null;
        loadStripe()
            .then(stripe => {
                if (cancelled || !mountRef.current) return;
                stripeRef.current = stripe;
                const elements = stripe.elements({ clientSecret, appearance: STRIPE_APPEARANCE, locale: 'sv' });
                elementsRef.current = elements;
                const payment = elements.create('payment', {
                    layout: { type: 'tabs', defaultCollapsed: false },
                    defaultValues: { billingDetails: { email } },
                });
                payment.on('ready', () => !cancelled && setReady(true));
                payment.on('change', (e: any) => !cancelled && setComplete(!!e?.complete));
                payment.mount(mountRef.current);
                element = payment;
            })
            .catch(e => !cancelled && setError(e.message));
        return () => {
            cancelled = true;
            try { element?.unmount(); } catch { /* redan borta */ }
        };
    }, [clientSecret, email]);

    const pay = async () => {
        const stripe = stripeRef.current;
        const elements = elementsRef.current;
        if (!stripe || !elements) return;
        setPaying(true);
        setError(null);
        try {
            const submitted = await elements.submit();
            if (submitted.error) {
                setError(submitted.error.message || 'Kontrollera betaluppgifterna.');
                return;
            }
            onBeforeRedirect();
            const result = await stripe.confirmPayment({
                elements,
                confirmParams: { return_url: stripeReturnUrl(), receipt_email: email },
                redirect: 'if_required',
            });
            if (result.error) {
                setError(result.error.message || 'Betalningen gick inte igenom. Försök igen eller välj ett annat betalsätt.');
                return;
            }
            const status = result.paymentIntent?.status;
            if (status === 'succeeded' || status === 'processing') onPaid(status);
            else setError('Betalningen blev inte klar. Försök igen.');
        } finally {
            setPaying(false);
        }
    };

    return (
        <div className="space-y-5">
            <button type="button" onClick={onBack} disabled={paying} className="flex items-center gap-1.5 text-xs font-bold text-stone-500 hover:text-coffee-600 transition-colors disabled:opacity-50">
                <ArrowLeft size={14} /> Ändra uppgifter
            </button>

            <div>
                <h4 className="text-xs font-black uppercase tracking-widest text-stone-400 mb-3 px-1">Betala</h4>
                <div className="bg-stone-50 border border-stone-100 rounded-xl p-4 space-y-1.5 text-sm">
                    <div className="flex justify-between text-stone-600">
                        <span>Varor, {itemCount} {itemCount === 1 ? 'produkt' : 'produkter'}</span>
                        <span className="font-bold">{kr(goods)}</span>
                    </div>
                    {isHome ? (
                        <div className="flex justify-between text-stone-600">
                            <span>Frakt{weightGrams ? `, ${formatKg(weightGrams)}` : ''}</span>
                            <span className="font-bold">{kr(shipping)}</span>
                        </div>
                    ) : (
                        <div className="flex justify-between text-stone-600">
                            <span>Hämtas hos säljaren</span>
                            <span className="font-bold">0 kr</span>
                        </div>
                    )}
                    <div className="flex justify-between items-baseline pt-2 mt-1 border-t border-dashed border-stone-200">
                        <span className="text-xs font-black uppercase tracking-widest text-stone-500">Att betala</span>
                        <span className="text-xl font-serif font-bold text-coffee-900">{kr(total)}</span>
                    </div>
                    <VatLines lines={vatBreakdown} />
                </div>
            </div>

            {stripeTestMode() && (
                <p className="text-[11px] leading-snug text-amber-800 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                    <strong>Testläge.</strong> Inga riktiga pengar dras. Testkort: 4242 4242 4242 4242, valfritt datum framåt och valfri CVC.
                </p>
            )}

            <div className="min-h-[180px]">
                {!ready && !error && (
                    <div className="space-y-3 animate-pulse" aria-hidden="true">
                        <div className="h-12 rounded-xl bg-stone-100" />
                        <div className="h-12 rounded-xl bg-stone-100" />
                        <div className="h-12 rounded-xl bg-stone-100" />
                    </div>
                )}
                <div ref={mountRef} className={ready ? '' : 'h-0 overflow-hidden'} />
            </div>

            {error && (
                <div role="alert" className="flex gap-2 items-start text-xs text-red-700 bg-red-50 border border-red-100 rounded-xl p-3">
                    <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}

            <Button type="button" onClick={pay} isLoading={paying} disabled={!ready || !complete || paying} icon={!paying && <Lock size={16} />}>
                {paying ? 'Betalar...' : `Betala ${kr(total)}`}
            </Button>

            <div className="flex items-center justify-center gap-2 text-[10px] text-stone-400 pb-4">
                <ShieldCheck size={12} />
                <span>Säker betalning via Stripe till CoffeeNova</span>
            </div>
        </div>
    );
};
